import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useCRMData } from '../../context/CRMDataContext';
import { useTenant } from '../../context/TenantContext';
import SafeIcon from '../../common/SafeIcon';

const TAG_LIBRARY_KEY = 'saleswork_demo_tag_library';
const NEW_DEAL_DRAFT_KEY = 'saleswork_new_deal_draft';
const DEFAULT_TAG_COLORS = ['blue', 'green', 'purple', 'orange', 'red', 'gray'];

const genId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const normalizeTags = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === 'string') {
    return value
      .split(',')
      .map(tag => tag.trim())
      .filter(Boolean);
  }
  return [];
};

const getStoredNewDealDraft = () => {
  try {
    const rawDraft = sessionStorage.getItem(NEW_DEAL_DRAFT_KEY);
    const parsedDraft = rawDraft ? JSON.parse(rawDraft) : null;

    return parsedDraft && typeof parsedDraft === 'object' ? parsedDraft : null;
  } catch {
    return null;
  }
};

const saveNewDealDraft = (formData, tagsInput) => {
  try {
    sessionStorage.setItem(
      NEW_DEAL_DRAFT_KEY,
      JSON.stringify({ ...formData, tagsInput })
    );
  } catch {}
};

const clearNewDealDraft = () => {
  try {
    sessionStorage.removeItem(NEW_DEAL_DRAFT_KEY);
  } catch {}
};

const getTagLibrary = () => {
  try {
    const raw = localStorage.getItem(TAG_LIBRARY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveTagLibrary = (library) => {
  try {
    localStorage.setItem(TAG_LIBRARY_KEY, JSON.stringify(library));
  } catch {}
};

const getOrCreateTagMeta = (tagName) => {
  const name = String(tagName || '').trim();
  if (!name) return null;

  const library = getTagLibrary();
  const existing = library.find(
    tag => tag?.name?.toLowerCase() === name.toLowerCase()
  );

  if (existing) return existing;

  const next = {
    name,
    color: DEFAULT_TAG_COLORS[library.length % DEFAULT_TAG_COLORS.length]
  };

  saveTagLibrary([...library, next]);
  return next;
};

const getDomainFromUrl = (url) => {
  try {
    if (!url) return '';
    const normalized = url.startsWith('http') ? url : `https://${url}`;
    return new URL(normalized).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const getLogoUrlFromWebsite = (website) => {
  const domain = getDomainFromUrl(website);
  return domain
    ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128`
    : '';
};

// Gjenbrukbar Accordion-seksjon
const FormSection = ({ title, icon, isOpen, onToggle, children }) => (
  <div className="border-b border-slate-100 last:border-0">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between py-4 px-1 text-left group hover:bg-slate-50/50 transition-colors"
    >
      <div className="flex items-center gap-3">
        {icon && <SafeIcon icon={icon} className="text-slate-400 w-4 h-4" />}
        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider group-hover:text-blue-600 transition-colors">
          {title}
        </span>
      </div>
      <SafeIcon icon="▼" className={`text-slate-400 w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    {isOpen && (
      <div className="pb-6 animate-in slide-in-from-top-1 duration-200">
        {children}
      </div>
    )}
  </div>
);

const DealForm = ({ deal, onClose }) => {
  const { currentUser } = useAuth();
  const {
    addDeal,
    updateDeal,
    addCompany,
    updateCompany,
    addContact,
    updateContact,
    companies,
    contacts,
    pipelineStages
  } = useCRMData();
  const users = [];
  const { activeTenantId, activeTenant } = useTenant();
  const selectedTenantId = activeTenantId || activeTenant?.id || null;
  const pipelineContextName = deal?.pipelineName || null;
  const [saveError, setSaveError] = useState('');

  const defaultOwner =
    currentUser?.name ||
    currentUser?.fullName ||
    currentUser?.email ||
    'Start Demo';

  const defaultOwnerId = currentUser?.user_id || currentUser?.id || '';

  const [openSections, setOpenSections] = useState({
    contactInfo: true,
    dealInfo: false,
    customFields: false,
    billingDetails: false,
    updates: true
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const initialFormState = {
    leadName: deal?.contactName || deal?.name || '',
    name: deal?.contactName || deal?.name || '',
    contactName: deal?.contactName || deal?.name || '',
    email: deal?.email || '',
    phone: deal?.phone || '',
    companyName: deal?.companyName || deal?.company || '',
    company: deal?.companyName || deal?.company || '',
    jobTitle: deal?.jobTitle || deal?.title || '',
    title: deal?.jobTitle || deal?.title || '',
    website: deal?.website || '',
    logoUrl: deal?.logoUrl || deal?.companyLogo || '',
    companyLogo: deal?.companyLogo || deal?.logoUrl || '',
    source: deal?.source || '',
    dealTitle: deal?.dealTitle || '',
    value: deal?.value || '',
    currency: 'NOK',
    stage_id: deal?.stage_id || pipelineStages[0]?.stage_id || '',
    pipeline_id: deal?.pipeline_id || deal?.pipelineId || null,
    pipelineId: deal?.pipelineId || deal?.pipeline_id || null,
    client_company_id: deal?.client_company_id || deal?.clientCompanyId || null,
    clientCompanyId: deal?.clientCompanyId || deal?.client_company_id || null,
    status: deal?.status || 'Ny',
    probability: deal?.probability ?? '',
    expected_close_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    owner_user_id: deal?.owner_user_id || defaultOwnerId || '',
    owner_id: deal?.owner_id || deal?.owner_user_id || defaultOwnerId || users[0]?.user_id || null,
    owner: deal?.owner ?? deal?.ownerName ?? defaultOwner,
    ownerName: deal?.ownerName ?? deal?.owner ?? defaultOwner,
    last_contacted: new Date().toISOString().split('T')[0],
    notes: '',
    tags: normalizeTags(deal?.tags || deal?.labels || deal?.tagList)
  };

  const [formData, setFormData] = useState(initialFormState);
  const [tagsInput, setTagsInput] = useState(
    normalizeTags(deal?.tags || deal?.labels || deal?.tagList).join(', ')
  );

  useEffect(() => {
    if (deal && (deal.deal_id || deal.id)) {
      const existingContact = contacts.find(c => c.contact_id === deal.contact_id);
      const existingCompany = companies.find(c => c.company_id === deal.company_id);
      const normalizedContact = existingContact || contacts.find(c => (
        String(c.contact_id || c.id || '') === String(deal.contact_id || '')
      ));
      const normalizedCompany = existingCompany || companies.find(c => (
        String(c.company_id || c.id || '') === String(deal.company_id || '')
      ));
      const contactName = deal.contactName || deal.name || existingContact?.name || '';
      const companyName = deal.companyName || deal.company || existingCompany?.name || '';
      const jobTitle = deal.jobTitle || deal.title || existingContact?.title || '';
      const normalizedDealTags = normalizeTags(deal.tags || deal.labels || deal.tagList);
      const existingStageId =
        deal.stage_id ||
        deal.stageId ||
        deal.pipelineStageId ||
        formData.stage_id ||
        pipelineStages[0]?.stage_id ||
        pipelineStages[0]?.id ||
        '';

      const nextFormData = {
        ...formData,
        leadName: contactName,
        name: contactName,
        contactName,
        email: deal.email || existingContact?.email || '',
        phone: deal.phone || existingContact?.phone || '',
        companyName,
        company: companyName,
        jobTitle,
        title: jobTitle,
        website: deal.website || existingCompany?.website || '',
        logoUrl: deal.logoUrl || deal.companyLogo || existingCompany?.logoUrl || existingCompany?.logo || '',
        companyLogo: deal.companyLogo || deal.logoUrl || existingCompany?.logo || existingCompany?.logoUrl || '',
        source: deal.source || '',
        dealTitle: deal.dealTitle || '',
        value: deal.value || '',
        stage_id: existingStageId,
        pipeline_id: deal.pipeline_id ?? deal.pipelineId ?? formData.pipeline_id ?? null,
        pipelineId: deal.pipelineId ?? deal.pipeline_id ?? formData.pipelineId ?? null,
        client_company_id: deal.client_company_id ?? deal.clientCompanyId ?? formData.client_company_id ?? null,
        clientCompanyId: deal.clientCompanyId ?? deal.client_company_id ?? formData.clientCompanyId ?? null,
        status: deal.status || formData.status || 'Ny',
        probability: deal.probability ?? '',
        expected_close_date: deal.expected_close_date || formData.expected_close_date,
        owner_user_id: deal.owner_user_id !== undefined && deal.owner_user_id !== null
          ? deal.owner_user_id
          : formData.owner_user_id,
        owner_id: deal.owner_id !== undefined && deal.owner_id !== null
          ? deal.owner_id
          : deal.owner_user_id !== undefined && deal.owner_user_id !== null
            ? deal.owner_user_id
            : formData.owner_id,
        owner: deal.owner ?? deal.ownerName ?? formData.owner,
        ownerName: deal.ownerName ?? deal.owner ?? formData.ownerName,
        notes: deal.notes || '',
        tags: normalizedDealTags
      };

      console.log('[DEALFORM EDIT INIT]', nextFormData);
      setFormData(nextFormData);
      setTagsInput(normalizedDealTags.join(', '));
    } else {
      setTagsInput('');
    }
  }, [deal, contacts, companies, pipelineStages]);

  useEffect(() => {
    if (deal?.deal_id || deal?.id) return;

    const draft = getStoredNewDealDraft();

    if (!draft) return;

    const { tagsInput: storedTagsInput, ...storedFormData } = draft;

    setFormData(prev => ({
      ...prev,
      ...storedFormData,
      tags: normalizeTags(storedFormData.tags || prev.tags)
    }));

    if (storedTagsInput !== undefined) {
      setTagsInput(storedTagsInput);
    }
  }, [deal?.deal_id, deal?.id]);

  useEffect(() => {
    if (deal?.deal_id || deal?.id) return;

    saveNewDealDraft(formData, tagsInput);
  }, [deal?.deal_id, deal?.id, formData, tagsInput]);

  const resetForm = () => {
    clearNewDealDraft();
    setFormData({
      ...initialFormState,
      stage_id: formData.stage_id,
      owner_user_id: formData.owner_user_id,
      owner_id: formData.owner_id,
      owner: defaultOwner,
      ownerName: defaultOwner
    });
    setTagsInput('');
    setSaveError('');
  };

  const handleClose = () => {
    clearNewDealDraft();
    onClose();
  };

  const handleSubmit = async (e, action = 'save') => {
    e.preventDefault();
    setSaveError('');

    if (!selectedTenantId) {
      setSaveError('Ingen aktiv organisasjon er valgt. Velg HMS Varsling og prøv igjen.');
      return;
    }

    const contactName = (formData.name || formData.leadName || '').trim();
    const companyName = (formData.company || formData.companyName || '').trim();
    const jobTitle = (formData.title || formData.jobTitle || '').trim();
    const owner = formData.owner ?? defaultOwner;
    const ownerName = formData.ownerName !== undefined && formData.ownerName !== null
      ? formData.ownerName
      : owner;
    const ownerId = formData.owner_user_id !== undefined && formData.owner_user_id !== null
      ? formData.owner_user_id
      : defaultOwnerId || null;
    const website = formData.website?.trim() || '';
    const logoUrl = website
      ? formData.logoUrl || formData.companyLogo || getLogoUrlFromWebsite(website)
      : '';

    try {
      let compId = deal?.company_id;

      if (compId) {
        const companyResult = await updateCompany(String(compId), {
          name: companyName,
          website
        });

        if (companyResult?.success === false) {
          console.error('[DEALFORM COMPANY UPDATE]', companyResult.error);
          throw companyResult.error;
        }
      }

      if (!compId && companyName) {
        const companyResult = await addCompany({
          company_id: genId('comp'),
          name: companyName,
          website,
          status: 'Lead'
        });

        if (companyResult?.success === false) {
          console.error('[DEALFORM COMPANY SAVE]', companyResult.error);
          throw companyResult.error;
        }

        compId = companyResult?.data?.id || companyResult?.data?.company_id;

        if (!compId) {
          console.error('[DEALFORM COMPANY SAVE] Selskapet ble lagret uten tilgjengelig ID.');
          throw new Error('Selskapet ble lagret uten tilgjengelig ID.');
        }
      }

      let contId = deal?.contact_id;

      if (contId) {
        const contactResult = await updateContact(String(contId), {
          company_id: compId ? String(compId) : null,
          name: contactName,
          email: formData.email,
          phone: formData.phone,
          title: jobTitle,
          role: jobTitle
        });

        if (contactResult?.success === false) {
          console.error('[DEALFORM CONTACT UPDATE]', contactResult.error);
          throw contactResult.error;
        }
      }

      if (!contId && contactName) {
        const contactResult = await addContact({
          contact_id: genId('cont'),
          company_id: compId,
          name: contactName,
          email: formData.email,
          phone: formData.phone,
          title: jobTitle,
          role_in_buying: 'Besluter'
        });

        if (contactResult?.success === false) {
          console.error('[DEALFORM CONTACT SAVE]', contactResult.error);
          throw contactResult.error;
        }

        contId = contactResult?.data?.id || contactResult?.data?.contact_id;

        if (!contId) {
          console.error('[DEALFORM CONTACT SAVE] Kontakten ble lagret uten tilgjengelig ID.');
          throw new Error('Kontakten ble lagret uten tilgjengelig ID.');
        }
      }

      const finalDealTitle = formData.dealTitle || (formData.companyName ? `${formData.companyName}` : `${formData.leadName}`) || 'Ny Lead';
      const normalizedTags = normalizeTags(tagsInput);

      normalizedTags.forEach(tag => getOrCreateTagMeta(tag));

      const dealPayload = Object.fromEntries(
        Object.entries(formData).filter(([key]) => key !== 'tenant_id' && key !== 'tenantId')
      );

      const preservedStageId =
        formData.stage_id ||
        deal?.stage_id ||
        deal?.stageId ||
        deal?.pipelineStageId ||
        null;

      const dealData = {
        ...dealPayload,
        title: finalDealTitle,
        name: contactName,
        contactName,
        company: companyName,
        companyName,
        jobTitle,
        email: formData.email || '',
        phone: formData.phone || '',
        website,
        logoUrl,
        companyLogo: logoUrl,
        dealTitle: finalDealTitle,
        value: Number(formData.value) || 0,
        status: formData.status || deal?.status || 'Ny',
        probability: formData.probability === '' ? 0 : Number(formData.probability),
        expected_close_date: formData.expected_close_date,
        company_id: compId || null,
        contact_id: contId || null,
        pipeline_id: formData.pipeline_id || formData.pipelineId || null,
        pipelineId: formData.pipelineId || formData.pipeline_id || null,
        client_company_id: formData.client_company_id || formData.clientCompanyId || null,
        clientCompanyId: formData.clientCompanyId || formData.client_company_id || null,
        ...(preservedStageId ? { stage_id: preservedStageId } : {}),
        owner_user_id: formData.owner_user_id !== undefined && formData.owner_user_id !== null
          ? formData.owner_user_id
          : ownerId,
        owner_id: ownerId,
        owner,
        ownerName,
        source: formData.source,
        notes: formData.notes,
        tags: normalizedTags
      };

      console.log('[DEALFORM SAVE PAYLOAD]', dealData);

      console.log('[TAGS FORM SAVE]', {
        tagsInput,
        normalizedTags,
        payloadTags: dealData.tags,
        dealData
      });

      const dealId = deal?.deal_id || deal?.id;
      const dealSaveResult = dealId
        ? await updateDeal(String(dealId), dealData)
        : await addDeal(dealData);

      console.log('[TAGS FORM RESULT]', dealSaveResult);

      if (dealSaveResult?.success === false) {
        throw dealSaveResult.error;
      }

      if (action === 'saveAndNew') {
        clearNewDealDraft();
        resetForm();
      } else {
        clearNewDealDraft();
        onClose();
      }
    } catch (error) {
      console.error('[DEALFORM SAVE]', error);
      setSaveError(error?.message || 'Kunne ikke lagre. Kontroller organisasjon og prøv igjen.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'leadName') {
      setFormData(prev => ({
        ...prev,
        leadName: value,
        name: value,
        contactName: value
      }));
      return;
    }

    if (name === 'companyName') {
      setFormData(prev => ({
        ...prev,
        companyName: value,
        company: value
      }));
      return;
    }

    if (name === 'jobTitle') {
      setFormData(prev => ({
        ...prev,
        jobTitle: value,
        title: value
      }));
      return;
    }

    if (name === 'owner_user_id') {
      const selectedUser = users.find(user => user.user_id === value);
      const selectedOwner = selectedUser?.name || selectedUser?.email || '';

      setFormData(prev => ({
        ...prev,
        owner_user_id: value,
        owner_id: value || null,
        owner: selectedOwner,
        ownerName: selectedOwner
      }));
      return;
    }

    if (name === 'tags') {
      setFormData(prev => ({ ...prev, tags: normalizeTags(value) }));
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleAddTag = () => {
    const newTag = prompt('Skriv inn ny tag:');
    if (newTag && newTag.trim()) {
      if (!formData.tags.includes(newTag.trim())) {
        setFormData(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
      }
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tagToRemove) }));
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="flex justify-end px-6 py-2 bg-slate-50 border-b border-slate-100 mb-2">
        <button className="flex items-center text-xs text-slate-500 hover:text-blue-600 gap-2">
          <span>Lær mer</span>
          <SafeIcon icon="📘" className="w-3 h-3 text-blue-400" />
          <SafeIcon icon="🎥" className="w-3 h-3 text-red-500" />
        </button>
      </div>

      <form className="flex-1 overflow-y-auto px-6 custom-scrollbar" onSubmit={(e) => handleSubmit(e, 'save')}>
        {pipelineContextName && (
          <p className="text-xs text-slate-500 py-2">
            Denne leaden opprettes i: {pipelineContextName}
          </p>
        )}

        {/* SECTION 1: CONTACT INFO */}
        <FormSection title="KONTAKTINFO" icon="👤" isOpen={openSections.contactInfo} onToggle={() => toggleSection('contactInfo')}>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <div className="col-span-2 md:col-span-1">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <span className="text-red-500 mr-1">*</span>Navn
              </label>
              <input required name="leadName" value={formData.name} onChange={handleChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" />
            </div>
            <div className="hidden md:block"></div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">E-post</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Telefon</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Selskap</label>
              <input name="companyName" value={formData.company} onChange={handleChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Tittel / Stilling</label>
              <input name="jobTitle" value={formData.title} onChange={handleChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nettside</label>
              <input name="website" value={formData.website} onChange={handleChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Kilde</label>
              <select name="source" value={formData.source} onChange={handleChange} className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white transition-all">
                <option value="">Velg kilde</option>
                <option value="Nettside">Nettside</option>
                <option value="Referanse">Referanse</option>
                <option value="Kaldt anrop">Kaldt anrop</option>
                <option value="LinkedIn">LinkedIn</option>
              </select>
            </div>
          </div>
        </FormSection>

        {/* SECTION 2: DEAL INFO */}
        <FormSection title="SALGSINFO" icon="💎" isOpen={openSections.dealInfo} onToggle={() => toggleSection('dealInfo')}>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mål / Tittel (Valgfritt)</label>
              <input name="dealTitle" value={formData.dealTitle} onChange={handleChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="Auto-genereres hvis tom" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Beløp</label>
              <div className="relative">
                <input type="number" name="value" value={formData.value} onChange={handleChange} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-1 focus:border-blue-500 outline-none transition-all" placeholder="0" />
                <span className="absolute right-3 top-2 text-slate-400 text-sm">{formData.currency}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fase</label>
              <select name="stage_id" value={formData.stage_id} onChange={handleChange} className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all">
                {pipelineStages.map(s => <option key={s.stage_id} value={s.stage_id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Sannsynlighet (%)</label>
              <input type="number" name="probability" value={formData.probability} onChange={handleChange} className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Forventet dato</label>
              <input type="date" name="expected_close_date" value={formData.expected_close_date} onChange={handleChange} className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" />
            </div>
          </div>
        </FormSection>

        {/* SECTION 3: CUSTOM FIELDS */}
        <FormSection title="EGENDEFINERTE FELT" icon="✨" isOpen={openSections.customFields} onToggle={() => toggleSection('customFields')}>
          <p className="text-xs text-slate-400 italic px-1">Ingen egendefinerte felt satt opp for øyeblikket.</p>
        </FormSection>

        {/* SECTION 4: BILLING DETAILS */}
        <FormSection title="FAKTURADETALJER" icon="💼" isOpen={openSections.billingDetails} onToggle={() => toggleSection('billingDetails')}>
          <p className="text-xs text-slate-400 italic px-1">Fylles inn senere i prosessen.</p>
        </FormSection>

        {/* SECTION 5: ADD UPDATES */}
        <FormSection title="OPPDATERINGER" icon="📝" isOpen={openSections.updates} onToggle={() => toggleSection('updates')}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Eier</label>
                <select name="owner_user_id" value={formData.owner_user_id} onChange={handleChange} className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all">
                  <option value="">Ingen eier</option>
                  {currentUser && <option key={currentUser.user_id || currentUser.id} value={currentUser.user_id || currentUser.id}>{currentUser.name || currentUser.fullName || currentUser.email}</option>}
                  {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Sist kontaktet</label>
                <input type="date" name="last_contacted" value={formData.last_contacted} onChange={handleChange} className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" />
              </div>
            </div>

            {/* Tags funksjonalitet */}
            <div className="pt-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">Tags / Etiketter</label>
              <input
                type="text"
                name="tags"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="Skriv etiketter separert med komma"
                className="w-full mb-3 px-3 py-2 border border-slate-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
              <p className="text-xs text-slate-400 mb-3">Varm lead, HMS, Rekruttering</p>
              <div className="flex flex-wrap gap-2 items-center">
                {formData.tags.map((tag, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-100 rounded-md text-xs font-bold">
                    {tag}
                    <button type="button" onClick={() => handleRemoveTag(tag)} className="hover:text-rose-500 ml-1">
                      <SafeIcon icon="✕" className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <button type="button" onClick={handleAddTag} className="text-blue-600 font-bold text-sm flex items-center hover:underline px-2 py-1 rounded bg-slate-50 hover:bg-blue-50 transition-colors border border-dashed border-slate-300 hover:border-blue-300">
                  <SafeIcon icon="➕" className="w-3 h-3 mr-1" /> Legg til tag
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Legg til nytt notat</label>
              <textarea name="notes" rows="3" value={formData.notes} onChange={handleChange} className="w-full p-3 border border-yellow-200 bg-yellow-50 rounded-md text-sm text-slate-800 placeholder-slate-400 focus:ring-1 focus:ring-yellow-400 focus:border-yellow-400 outline-none resize-none transition-all" placeholder="Legg til notater her..." />
            </div>
          </div>
        </FormSection>
      </form>

      {saveError && (
        <div role="alert" className="mx-6 mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {saveError}
        </div>
      )}

      {/* FOOTER */}
      <div className="px-6 py-4 border-t border-slate-200 flex justify-between items-center bg-white mt-auto">
        <button onClick={handleClose} className="text-blue-600 font-medium text-sm hover:underline">
          Avbryt
        </button>
        <div className="flex gap-3">
          <button type="button" onClick={(e) => handleSubmit(e, 'saveAndNew')} className="px-4 py-2 bg-white text-slate-700 font-medium text-sm rounded border border-slate-300 hover:bg-slate-50 transition-colors">
            Lagre & Ny
          </button>
          <button type="button" onClick={(e) => handleSubmit(e, 'save')} className="px-6 py-2 bg-blue-600 text-white font-medium text-sm rounded hover:bg-blue-700 transition-colors shadow-sm">
            Lagre
          </button>
        </div>
      </div>
    </div>
  );
};

export default DealForm;